import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { Plus, MapPin, Clock, HelpCircle } from "lucide-react";
import { eventLabel } from "@shared/eventTypes";
import { formatTimeToDisplay } from "../utils/dateUtils";
import { EventPoster } from "./EventPoster";
import { ProfileImage } from "./ProfileImage";
import { DateBadge } from "./ux/DateBadge";
import { CreateKikiModal } from "./CreateKikiModal";
import { KikiHelpModal } from "./KikiHelpModal";
import { SubscribeInterstitial } from "./SubscribeInterstitial";
import { KIKI_CATEGORIES, getKikiCategoryByEmoji, DEFAULT_KIKI_GRADIENT } from "../lib/kikiTemplates";
import { useAuth } from "../lib/platform";
import { useOnboardingGate } from "./OnboardingContext";
import { useIsSubscriber } from "../lib/api/useProfile";
import type { OpenKiki, KikiParticipant } from "../lib/api/useEventsList";
import tracking from "../lib/tracking";

// Adapts a kiki participant (carries a last-initial, not a full last name) to
// the shared ProfileImage so avatars look identical to the rest of the app.
const asProfileUser = (p: KikiParticipant) => ({
  firstName: p.firstName,
  lastName: p.lastInitial,
  profileImageUrl: p.profileImageUrl,
});

const participantName = (p: KikiParticipant) =>
  `${p.firstName}`;

const ACTIVITY_EMOJI: Record<string, string> = {
  Movies: "🎬",
  Bowling: "🎳",
  Hiking: "🥾",
  Coffee: "☕",
  "Dinner out": "🍕",
  Museum: "🎨",
  "Beach day": "🏖️",
  Gaming: "🎮",
};

// Fraction of the viewport a single card occupies — the remainder reveals a
// peek of the next card, the way the reference carousel does.
const CARD_FRACTION = 0.80;
const CARD_MAX_PX = 460;
const GAP_PX = 0;

// Avatar row geometry. Each avatar occupies a fixed slot so the row never
// reflows; the active one scales up (about its top edge) to overlap the card.
const AV_SMALL = 40; // resting diameter (matches ProfileImage "md")
const AV_GAP = 10;
const AV_SLOT = AV_SMALL + AV_GAP;
const AV_SCALE = 1.55; // active ≈ 68px
const AV_ACTIVE_SPREAD = 14; // extra room pushed open on each side of the active avatar
const AV_ANCHOR_LEFT = 16; // card / row left inset
// The card's rounded-3xl corner curves inward, so push the active avatar a
// touch further in than the card edge to seat it against that curve.
const AV_CURVE_INSET = 12;

// Kikis stand on their own URL — the kiki fully defines the event it's for.
function kikiHref(kiki: OpenKiki): string {
  return `/kiki/${kiki.id}`;
}

// The large featured card — the kiki's event (or a standalone activity tile).
// Memoized so that when `active` changes only the two affected cards re-render
// (not the whole rail), keeping the main thread free for the scroll animation.
const KikiFeatureCard = memo(function KikiFeatureCard({
  kiki,
  active,
}: {
  kiki: OpenKiki;
  active: boolean;
}) {
  const isStandalone = kiki.event === null;
  const activityEmoji =
    kiki.activityInfo?.emoji ?? (kiki.activity ? ACTIVITY_EMOJI[kiki.activity] ?? "✨" : "✨");
  // Match the category by emoji so the card echoes the vibe picked at
  // creation — a category photo when we have one, else its gradient.
  const category = getKikiCategoryByEmoji(activityEmoji);
  const categoryGradient = category?.gradient ?? DEFAULT_KIKI_GRADIENT;
  const categoryImage = category?.image ?? null;
  const activityName = kiki.activityInfo?.name ?? kiki.activity;
  const href = kikiHref(kiki);

  const name = isStandalone ? activityName ?? "Hang out" : kiki.event!.name;
  // Date/time come from the event, or from the standalone activity info.
  const dateStr = isStandalone ? kiki.activityInfo?.date ?? null : kiki.event!.date;
  const timeStr = isStandalone ? kiki.activityInfo?.time ?? null : kiki.event!.time;
  const timeLabel = timeStr ? formatTimeToDisplay(timeStr) : null;
  const venueLine = isStandalone
    ? kiki.activityInfo?.location ?? null
    : [kiki.event!.venueName, kiki.event!.venueCity].filter(Boolean).join(" · ") || null;

  return (
    <div
      className={`relative w-full transition-transform duration-300 ${
        active ? "scale-100" : "scale-[0.94]"
      }`}
      style={{ aspectRatio: "1" }}
    >
      {/* Clipped media surface. The scale lives on the parent, so this layer is
          untransformed and clips reliably — Safari otherwise drops the
          rounded-corner clip once a transform settles. clip-path is belt-and-
          braces; radius matches rounded-3xl (1.5rem). */}
      <div
        className="absolute inset-0 overflow-hidden rounded-3xl"
        style={{ clipPath: "inset(0 round 1.5rem)" }}
      >
      <Link
        href={href}
        onClick={() => tracking.kikiCarouselCardClicked(kiki.id, isStandalone ? "standalone" : "event_tied")}
        className="block w-full h-full group"
      >
        {/* Background visual */}
        {isStandalone ? (
          categoryImage ? (
            <img
              src={categoryImage}
              alt=""
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              draggable={false}
            />
          ) : (
            <div className={`absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br ${categoryGradient}`}>
              <span className="text-7xl drop-shadow-lg">{activityEmoji}</span>
            </div>
          )
        ) : kiki.event!.coverImageUrl ? (
          <img
            src={kiki.event!.coverImageUrl}
            alt={kiki.event!.name ?? ""}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            draggable={false}
          />
        ) : (
          <EventPoster
            name={kiki.event!.name ?? ""}
            label={eventLabel(kiki.event!.types)}
            types={kiki.event!.types}
            className="absolute inset-0 w-full h-full"
          />
        )}

        {/* Emoji corner tab — small orange marker, only on the selected card. */}
        <div
          className={`absolute top-0 right-0 px-3.5 py-2.5 rounded-bl-2xl bg-primary-500 text-white flex items-center justify-center shadow-md transition-opacity duration-300 ${
            active ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <span className="text-lg leading-none">{activityEmoji}</span>
        </div>

        {/* Bottom info panel — its own frosted card now that there's no
            full-card gradient, so the text stays readable over any image. */}
        <div className="absolute inset-x-2 bottom-2">
          <div className="rounded-2xl bg-black/60 backdrop-blur-md ring-1 ring-black/30 p-2">
            {/* Compact row: date indicator + name/location */}
            <div className="flex items-center gap-3">
              {dateStr && <DateBadge date={dateStr} size="md" />}
              <div className="min-w-0 flex-1">
                <h3 className="text-lg sm:text-xl font-bold text-white leading-tight line-clamp-1">
                  {name}
                </h3>
                {(venueLine || timeLabel) && (
                  <p className="mt-0.5 flex items-center gap-1.5 text-sm text-white/65">
                    {venueLine ? (
                      <MapPin className="w-3.5 h-3.5 shrink-0" />
                    ) : (
                      <Clock className="w-3.5 h-3.5 shrink-0" />
                    )}
                    <span className="truncate">{[venueLine, timeLabel].filter(Boolean).join(" · ")}</span>
                  </p>
                )}
              </div>
            </div>

            {kiki.note && (
              <p className="mt-2 text-sm italic leading-snug text-white/85 line-clamp-2">
                “{kiki.note}”
              </p>
            )}
            {/* Facepile of the confirmed group + their names */}
            <div className="flex items-center gap-2 mt-2">
              <div className="flex -space-x-2 shrink-0">
                {kiki.participants.slice(0, 4).map((p) => (
                  <ProfileImage
                    key={p.id}
                    size="xs"
                    user={asProfileUser(p)}
                  />
                ))}
              </div>
              <p className="min-w-0 flex-1 truncate text-xs font-semibold text-white/85">
                {kiki.participants.map(participantName).join(", ")}
              </p>
            </div>
          </div>
        </div>
      </Link>
      </div>

      {/* Active ring — its own layer so the border never shares the clipped,
          scaled surface (where Safari glitches it). Constant width = no layout
          shift; only opacity animates. */}
      <div
        aria-hidden
        className={`absolute inset-0 rounded-3xl border-4 border-primary-500 pointer-events-none transition-opacity duration-300 ${
          active ? "opacity-100" : "opacity-0"
        }`}
      />
    </div>
  );
});

const DEFAULT_KIKI_TITLE = "Let's have a Kiki";
const DEFAULT_KIKI_SUBTITLE =
  "Meet like-minded people and head out together — connect over a shared plan and turn up as a crew.";

export function KikiCarousel({
  kikis,
  cityId,
  isSignedIn,
  title = DEFAULT_KIKI_TITLE,
  subtitle = DEFAULT_KIKI_SUBTITLE,
  showCreate = true,
}: {
  kikis: OpenKiki[];
  cityId?: string | null;
  isSignedIn: boolean;
  // Header copy + whether to show the "start your own" template picker. Lets
  // the same carousel back both the browse rail and the dashboard "My Kikis".
  title?: string;
  subtitle?: string | null;
  showCreate?: boolean;
}) {
  const [createOpen, setCreateOpen] = useState(false);
  const [createCategoryId, setCreateCategoryId] = useState<string | undefined>(undefined);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const { openSignIn } = useAuth();
  const onboarding = useOnboardingGate();
  // On the events index a signed-out action opens the onboarding intro; elsewhere
  // fall back to the plain sign-in dialog.
  const gateSignIn = () => (onboarding ? onboarding.openOnboarding() : openSignIn());
  const isSubscriber = useIsSubscriber();

  // Creating a Kiki is layered: signed-out users are sent to sign-in, signed-in
  // non-subscribers hit the membership interstitial, subscribers get the form.
  const openCreate = (categoryId?: string) => {
    tracking.kikiCreateInitiated(categoryId ?? "unknown", "carousel");
    setCreateCategoryId(categoryId);
    if (!isSignedIn) {
      tracking.kikiSignInClicked("no_kikis");
      gateSignIn();
      return;
    }
    if (!isSubscriber) {
      tracking.kikiSubscribeRequiredShown();
      setSubscribeOpen(true);
      return;
    }
    setCreateOpen(true);
  };

  const viewportRef = useRef<HTMLDivElement>(null);
  const [viewportW, setViewportW] = useState(0);

  // The cards ride the browser's own (compositor-driven) scroller. We read
  // scrollLeft to drive the avatar row in lockstep, but write that animation
  // straight to the DOM via refs (see applyProgress) rather than through React
  // state — re-rendering on every scroll frame is what makes the heads stutter
  // on iOS. Only the active index, which changes once per card, lives in state.
  const [active, setActive] = useState(0);
  const stripRef = useRef<HTMLDivElement>(null);
  const avatarRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // The active-avatar ring is a separate composited layer whose opacity we
  // animate — far cheaper on iOS than repainting a box-shadow every frame.
  const ringRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const rafRef = useRef<number | null>(null);
  // Scroll-tracking loop bookkeeping (see onScroll/tick below).
  // `current` is the smoothed, displayed progress; it eases toward the live
  // scroll-derived target each frame so jittery/stale scrollLeft reads don't
  // show up as jitter in the strip. `lastTarget` + idle frames decide when the
  // motion has settled enough to stop the loop.
  const currentRef = useRef(0);
  const lastTargetRef = useRef(-1);
  const lastTsRef = useRef(0);
  const idleFramesRef = useRef(0);
  // Mirror of `active` so the loop can skip setState entirely when the centered
  // card hasn't changed (otherwise we'd queue 60 no-op updates/sec mid-scroll).
  const activeRef = useRef(0);

  // Desktop-only click-drag. Touch keeps the native scroller untouched; for a
  // mouse we nudge scrollLeft directly so the same native scroll (and the
  // onScroll handler below) does the work — just driven by the cursor.
  const dragRef = useRef<{ startX: number; startLeft: number } | null>(null);
  const movedRef = useRef(false);

  const cardW = viewportW ? Math.min(Math.round(viewportW * CARD_FRACTION), CARD_MAX_PX) : 0;
  const step = cardW + GAP_PX;
  // Room after the last card so it can still snap to the start edge instead of
  // being stuck against the right of the scroller.
  const trailPad = Math.max(0, viewportW - cardW - AV_ANCHOR_LEFT);

  // Paint the avatar row for a fractional card index, writing transforms
  // directly to the DOM so it tracks the native scroll without a React render.
  const applyProgress = (progress: number) => {
    const strip = stripRef.current;
    if (strip) {
      const t =
        AV_ANCHOR_LEFT + AV_CURVE_INSET + (AV_SMALL * (AV_SCALE - 1)) / 2 - progress * AV_SLOT;
      strip.style.transform = `translate3d(${t}px, 0, 0)`;
    }
    for (let i = 0; i < avatarRefs.current.length; i++) {
      const el = avatarRefs.current[i];
      if (!el) continue;
      const signed = i - progress;
      const c = 1 - Math.min(1, Math.abs(signed)); // 1 when active, 0 at a neighbour
      const scale = 1 + (AV_SCALE - 1) * c;
      // Push everything away from the active avatar so the gap beside it opens.
      const spread = AV_ACTIVE_SPREAD * Math.max(-1, Math.min(1, signed));
      // translate3d forces a GPU layer; transform + opacity are composited (no
      // per-frame repaint). The ring fades via its own layer's opacity.
      el.style.transform = `translate3d(${spread}px, 0, 0) scale(${scale})`;
      el.style.opacity = String(0.6 + 0.4 * c);
      el.style.zIndex = String(Math.round(c * 100));
      const ring = ringRefs.current[i];
      if (ring) ring.style.opacity = String(c);
    }
  };

  // Measure the viewport so we can size the cards and do the snap math in px,
  // and re-paint the heads since the per-card step changes with the card width.
  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      setViewportW(w);
      const cw = w ? Math.min(Math.round(w * CARD_FRACTION), CARD_MAX_PX) : 0;
      const progress = cw ? el.scrollLeft / cw : 0;
      currentRef.current = progress; // keep the smoother anchored to reality
      applyProgress(progress);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
  }, []);

  // The section is always shown (header + "start your own"), even with no open
  // kikis — only the avatar/card teaser is conditional on there being some.
  const hasKikis = kikis.length > 0;

  // iOS coalesces `scroll` events during momentum, so painting one frame per
  // event makes the heads jump. Instead, a scroll event kicks off a rAF loop
  // that polls scrollLeft every frame (smooth tracking regardless of how often
  // the OS reports scroll), and stops itself a few idle frames after motion
  // settles so it doesn't burn cycles when the rail is at rest.
  // Time constant of the easing, in seconds. Smaller = snappier (tracks the
  // finger more tightly); larger = smoother (more glide, more trailing).
  const SMOOTH_TAU = 0.14;

  const tick = (ts: number) => {
    const el = viewportRef.current;
    if (!el || !step) { rafRef.current = null; return; }

    const target = el.scrollLeft / step;
    // Frame-rate-independent exponential smoothing, so it feels the same on
    // 60Hz and 120Hz (ProMotion) displays.
    const dt = lastTsRef.current ? Math.min(0.05, (ts - lastTsRef.current) / 1000) : 0.016;
    lastTsRef.current = ts;
    const factor = 1 - Math.exp(-dt / SMOOTH_TAU);
    const cur = currentRef.current;
    const diff = target - cur;
    // Snap when within a sub-pixel of the target so the loop can actually rest.
    const next = Math.abs(diff) < 0.0008 ? target : cur + diff * factor;
    currentRef.current = next;
    applyProgress(next);

    // Active card follows the real (unsmoothed) position so scaling stays responsive.
    const idx = Math.max(0, Math.min(kikis.length - 1, Math.round(target)));
    if (idx !== activeRef.current) {
      activeRef.current = idx;
      setActive(idx);
    }

    // Settled = the strip has caught up AND the scroll target has stopped moving.
    if (next === target && target === lastTargetRef.current) {
      idleFramesRef.current++;
    } else {
      idleFramesRef.current = 0;
      lastTargetRef.current = target;
    }
    rafRef.current = idleFramesRef.current < 3 ? requestAnimationFrame(tick) : null;
  };

  const onScroll = () => {
    idleFramesRef.current = 0;
    if (rafRef.current == null) {
      lastTsRef.current = 0; // reset dt so the first frame after idle is sane
      rafRef.current = requestAnimationFrame(tick);
    }
  };

  // Tapping an avatar scrolls the matching card into view; the scroll handler
  // then animates everything in lockstep as the browser eases over.
  const goTo = (i: number) => {
    viewportRef.current?.scrollTo({ left: i * step, behavior: "smooth" });
  };

  const onPointerDown = (e: React.PointerEvent) => {
    // Touch/pen scroll natively — only intercept a mouse button.
    if (e.pointerType !== "mouse") return;
    const el = viewportRef.current;
    if (!el) return;
    dragRef.current = { startX: e.clientX, startLeft: el.scrollLeft };
    movedRef.current = false;
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const el = viewportRef.current;
    if (!el) return;
    const dx = e.clientX - dragRef.current.startX;
    // Only treat this as a drag (and capture the pointer) once it actually
    // moves — capturing on mousedown would retarget the click off the card's
    // link and break plain clicks.
    if (!movedRef.current && Math.abs(dx) > 4) {
      movedRef.current = true;
      // Free the cursor from snap while dragging; we re-snap on release.
      el.style.scrollSnapType = "none";
      el.setPointerCapture?.(e.pointerId);
    }
    if (!movedRef.current) return;
    el.scrollLeft = dragRef.current.startLeft - dx;
  };
  const endDrag = () => {
    if (!dragRef.current) return;
    const dragged = movedRef.current;
    dragRef.current = null;
    const el = viewportRef.current;
    if (!el || !step || !dragged) return;
    el.style.scrollSnapType = "x mandatory";
    el.scrollTo({ left: Math.round(el.scrollLeft / step) * step, behavior: "smooth" });
  };

  // Swallow the click that ends a drag so a card doesn't navigate on release.
  const onClickCapture = (e: React.MouseEvent) => {
    if (movedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      movedRef.current = false;
    }
  };

  return (
    <section>
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-1.5">
          <h2 className="text-lg sm:text-xl font-semibold">{title}</h2>
          <button
            type="button"
            onClick={() => { tracking.kikiHelpClicked("carousel"); setHelpOpen(true); }}
            className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
            aria-label="What is a kiki?"
          >
            <HelpCircle className="w-4 h-4" />
          </button>
        </div>
        {subtitle && (
          <p className="mt-0.5 text-sm text-neutral-600 dark:text-neutral-300">{subtitle}</p>
        )}
      </div>

      <div className="max-w-5xl mx-auto overflow-hidden">
        {!hasKikis ? (
          <div className="px-4">
            {!isSignedIn && (
              <button
                type="button"
                onClick={() => { tracking.kikiSignInClicked("no_kikis"); gateSignIn(); }}
                className="mt-3 px-5 py-2.5 rounded-full bg-neutral-900 dark:bg-primary-600 text-white text-sm font-semibold"
              >
                Sign in to start a Kiki
              </button>
            )}
          </div>
        ) : (
        /* Signed-out users get a teaser only: the Kikis and the people in them
            are blurred behind a sign-in prompt (first layer of visibility). */
        <div className="relative">
        <div
          className={isSignedIn ? undefined : "blur-[6px] pointer-events-none select-none"}
          aria-hidden={isSignedIn ? undefined : true}
        >
        {/* Avatar row — the people looking for a kiki. It slides in lockstep
            with the cards as they scroll, and the active avatar zooms up to
            overlap the card. The slide/zoom is written to the DOM imperatively
            (applyProgress) so it tracks the native scroll smoothly without a
            React render per frame. z-20 keeps the enlarged head above the card. */}
        <div className="relative z-20 overflow-visible mt-4" style={{ height: AV_SMALL }}>
          <div
            ref={stripRef}
            className="absolute left-0 top-0 flex items-start will-change-transform"
            // transform is set imperatively; only static layout stays in JSX.
            style={{ gap: AV_GAP }}
          >
            {kikis.map((kiki, i) => (
              <button
                key={kiki.id}
                ref={(el) => {
                  avatarRefs.current[i] = el;
                }}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Show ${kiki.user.firstName}'s kiki`}
                className="relative shrink-0 flex rounded-full will-change-transform"
                // transform / opacity / z-index are set imperatively.
                style={{ transformOrigin: "50% 0%" }}
              >
                <ProfileImage size="md" user={asProfileUser(kiki.user)} />
                {/* Active ring on its own composited layer — opacity animated. */}
                <span
                  ref={(el) => {
                    ringRefs.current[i] = el;
                  }}
                  aria-hidden
                  className="absolute inset-0 rounded-full pointer-events-none"
                  style={{
                    boxShadow: "0 0 0 3px rgba(239,98,37,1)",
                    opacity: 0,
                    willChange: "opacity",
                  }}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Card carousel. The active avatar (scaled ~68px from a 44px lane)
            hangs ~20px over this top edge. The cards ride the browser's own
            horizontal scroller with scroll-snap, so swiping is native and
            smooth (compositor-driven) instead of moved by JS. */}
        <div className="relative mt-2">
          <div
            ref={viewportRef}
            onScroll={onScroll}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onClickCapture={onClickCapture}
            onDragStart={(e) => e.preventDefault()}
            className="relative overflow-x-auto overflow-y-hidden cursor-grab active:cursor-grabbing [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            style={{
              scrollSnapType: "x mandatory",
              scrollPaddingLeft: AV_ANCHOR_LEFT,
              WebkitOverflowScrolling: "touch",
            }}
          >
            <div
              className="flex"
              style={{
                gap: GAP_PX,
                paddingLeft: AV_ANCHOR_LEFT,
                paddingRight: trailPad,
                width: "max-content",
              }}
            >
              {kikis.map((kiki, i) => (
                <div
                  key={kiki.id}
                  className="shrink-0"
                  style={{ width: cardW || "80%", scrollSnapAlign: "start" }}
                >
                  <KikiFeatureCard kiki={kiki} active={i === active} />
                </div>
              ))}
            </div>
          </div>
        </div>
        </div>
        {!isSignedIn && (
          <div className="absolute inset-0 z-30 flex items-center justify-center">
            <button
              type="button"
              onClick={() => { tracking.kikiSignInClicked("browse_overlay"); gateSignIn(); }}
              className="px-5 py-2.5 rounded-full bg-white/95 dark:bg-neutral-900/90 text-neutral-900 dark:text-white text-sm font-semibold shadow-lg backdrop-blur ring-1 ring-black/10"
            >
              Sign in to see Kikis
            </button>
          </div>
        )}
        </div>
        )}

        {/* Inline template picker — start a kiki straight from a vibe. */}
        {showCreate && isSignedIn && (
          <div className="mt-6 md:px-6 px-4">
            <h3 className="text-sm font-semibold text-base-content/80 mb-3">Create a custom event and start a Kiki!</h3>
            <div className="flex gap-4 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {KIKI_CATEGORIES.filter((c) => !c.custom).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => openCreate(c.id)}
                  className="shrink-0 flex flex-col items-center gap-1.5 w-16 group"
                >
                  <div
                    className={`w-14 h-14 rounded-full bg-gradient-to-br ${c.gradient} flex items-center justify-center text-2xl shadow-md transition-transform group-hover:scale-105 group-active:scale-95`}
                  >
                    <span style={{ filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.35))" }}>{c.emoji}</span>
                  </div>
                  <span className="text-xs font-medium text-base-content/70 text-center leading-tight">
                    {c.label}
                  </span>
                </button>
              ))}

              {/* Other — free-form activity with a pickable emoji */}
              <button
                type="button"
                onClick={() => openCreate("other")}
                className="shrink-0 flex flex-col items-center gap-1.5 w-16 group"
              >
                <div className="w-14 h-14 rounded-full border-2 border-dashed border-base-content/30 text-base-content/50 flex items-center justify-center transition-colors group-hover:border-primary-500 group-hover:text-primary-500 group-active:scale-95">
                  <Plus className="w-6 h-6" />
                </div>
                <span className="text-xs font-medium text-base-content/70 text-center leading-tight">
                  Other
                </span>
              </button>
            </div>
          </div>
        )}
      </div>

      <CreateKikiModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        cityId={cityId}
        initialCategoryId={createCategoryId}
      />

      <SubscribeInterstitial
        isOpen={subscribeOpen}
        onClose={() => setSubscribeOpen(false)}
        onSubscribed={() => setCreateOpen(true)}
      />

      <KikiHelpModal isOpen={helpOpen} onClose={() => setHelpOpen(false)} />
    </section>
  );
}
